import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Send, Sparkles, ChevronRight } from 'lucide-react';
import { logger } from './utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// EMPLOYER WIDGET — Mutual Interest list
// ---------------------------------------------------------------------------
export function MutualInterestWidget() {
  const [data, setData] = useState(null);
  const [requisitions, setRequisitions] = useState([]);
  const [inviting, setInviting] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/employer/mutual-interest`);
      setData(data);
    } catch (err) { logger.error('mutual interest', err); }
  }, []);

  useEffect(() => {
    load();
    // Load published reqs once so the invite picker is fast.
    axios.get(`${API}/cockpit/requisitions?published=true`)
      .then((r) => setRequisitions(Array.isArray(r.data) ? r.data.filter((x) => x.published && x.status !== 'closed') : []))
      .catch(() => {});
  }, [load]);

  const invite = async (membershipId, reqId) => {
    setInviting(membershipId);
    try {
      await axios.post(`${API}/employer/talent-pool/${membershipId}/invite-to-apply`, { requisition_id: reqId });
      window.alert('Invitation sent.');
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to invite');
    } finally { setInviting(null); }
  };

  return (
    <section
      data-testid="widget-mutual-interest"
      className="col-span-12 lg:col-span-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden"
    >
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <Heart className="w-4 h-4 text-rose-400" aria-hidden="true" />
          <h3 className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 dark:text-slate-400">
            Mutual Interest{data?.total != null ? ` · ${data.total}` : ''}
          </h3>
        </div>
        <button
          onClick={() => navigate('/cockpit')}
          className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 flex items-center gap-1"
          data-testid="mutual-interest-view-all"
        >
          Talent pool <ChevronRight className="w-3 h-3" aria-hidden="true" />
        </button>
      </header>
      <div className="p-5">
        {!data ? (
          <div className="h-24 bg-slate-100 dark:bg-slate-800 animate-pulse rounded" />
        ) : data.items.length === 0 ? (
          <div className="py-6 text-center text-slate-400 dark:text-slate-500">
            <Sparkles className="w-6 h-6 mx-auto mb-2" aria-hidden="true" />
            <p className="text-sm">No mutual interest yet. Express interest in a pool candidate to start a warm-lead pipeline.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800 -mx-1">
            {data.items.slice(0, 5).map((it) => (
              <li key={it.membership_id} className="py-3 px-1 flex items-center justify-between gap-3" data-testid={`mutual-item-${it.membership_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-50 truncate">{it.candidate_name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{it.ai_profile_excerpt || '—'}</p>
                </div>
                {requisitions.length > 0 && (
                  <InvitePicker reqs={requisitions} onPick={(rid) => invite(it.membership_id, rid)} busy={inviting === it.membership_id} testId={`invite-picker-${it.membership_id}`} />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function InvitePicker({ reqs, onPick, busy, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        data-testid={testId}
        className="px-2 py-1 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-1"
      >
        <Send className="w-3 h-3" aria-hidden="true" /> Invite
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-1 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg z-10 max-h-64 overflow-y-auto">
          {reqs.map((r) => (
            <button
              key={r.id}
              role="menuitem"
              onClick={() => { setOpen(false); onPick(r.id); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
            >
              {r.title} <span className="text-slate-400">· {r.country || '—'}</span>
            </button>
          ))}
          {reqs.length === 0 && <p className="p-2 text-xs text-slate-400">No published roles. Publish a requisition first.</p>}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// CANDIDATE BANNER — companies that have expressed interest in me
// ---------------------------------------------------------------------------
export function CandidateMutualInterestBanner() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/me/mutual-interest`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});
  }, []);

  if (items.length === 0) return null;

  return (
    <div
      data-testid="candidate-mutual-interest-banner"
      className="mb-6 rounded-xl bg-gradient-to-r from-rose-500 to-pink-600 text-white p-5 shadow-lg"
    >
      <div className="flex flex-wrap items-start gap-4 justify-between">
        <div className="flex items-start gap-3 flex-1 min-w-[240px]">
          <Heart className="w-6 h-6 mt-0.5 shrink-0" fill="currentColor" aria-hidden="true" />
          <div>
            <div className="text-xs uppercase tracking-wider text-rose-100 mb-1">Mutual interest</div>
            <div className="text-lg font-semibold" data-testid="mutual-interest-headline">
              {items.length} compan{items.length === 1 ? 'y is' : 'ies are'} interested in your profile
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {items.slice(0, 4).map((it) => (
                <span key={it.membership_id} className="text-xs bg-white/15 backdrop-blur px-2 py-0.5 rounded-full" data-testid={`mutual-chip-${it.membership_id}`}>
                  {it.company_name}{it.sector ? ` · ${it.sector}` : ''}
                </span>
              ))}
              {items.length > 4 && <span className="text-xs text-rose-100">+{items.length - 4} more</span>}
            </div>
          </div>
        </div>
        <Link
          to="/companies"
          data-testid="mutual-interest-cta"
          className="px-4 py-2 rounded-lg bg-white text-rose-700 font-semibold hover:bg-rose-50 transition text-sm shrink-0"
        >
          View companies
        </Link>
      </div>
    </div>
  );
}
